import React, { useContext, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Alert,
} from "@mui/material";
import { IRockPaperScissorsGame, IGameResult, IPenaltyItem } from "../ts/Definitions";
import { SelectedSettingsContext } from "../ts/Contexts";

interface RockPaperScissorsDialogProps {
  game: IRockPaperScissorsGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

const choices = ["Rock", "Paper", "Scissors"] as const;
type Choice = typeof choices[number];

function getRandomChoice(): Choice {
  return choices[Math.floor(Math.random() * choices.length)];
}

function getResult(player: Choice, ai: Choice): "win" | "lose" | "draw" {
  if (player === ai) return "draw";
  if (
    (player === "Rock" && ai === "Scissors") ||
    (player === "Paper" && ai === "Rock") ||
    (player === "Scissors" && ai === "Paper")
  ) {
    return "win";
  }
  return "lose";
}

const RockPaperScissorsDialog: React.FC<RockPaperScissorsDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { rounds, minPassRate, key, userid, username } = game;
  const [currentRound, setCurrentRound] = useState(1);
  const [playerWins, setPlayerWins] = useState(0);
  const [aiWins, setAiWins] = useState(0);
  const [draws, setDraws] = useState(0);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [lastPlayerChoice, setLastPlayerChoice] = useState<Choice | null>(null);
  const [lastAiChoice, setLastAiChoice] = useState<Choice | null>(null);

  const [penalties, setPenalties] = useState<(IPenaltyItem)[]>([]);

  useEffect(() => {
    const newPens = penaltyKeys?.map(key => settings.getPenalty(key)).filter(p => p !== undefined) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  const handlePlayerChoice = (choice: Choice) => {
    if (finished) return;
    const aiChoice = getRandomChoice();
    setLastPlayerChoice(choice);
    setLastAiChoice(aiChoice);
    const result = getResult(choice, aiChoice);
    if (result === "win") {
      setPlayerWins(w => w + 1);
      setMessage(`You win this round! (${choice} beats ${aiChoice})`);
    } else if (result === "lose") {
      setAiWins(w => w + 1);
      setMessage(`AI wins this round! (${aiChoice} beats ${choice})`);
    } else {
      setDraws(d => d + 1);
      setMessage(`Draw! (Both chose ${choice})`);
    }
    setTimeout(() => nextRound(), 1200);
  };

  const nextRound = () => {
    if (currentRound >= rounds) {
      setFinished(true);
    } else {
      setCurrentRound(r => r + 1);
      setLastPlayerChoice(null);
      setLastAiChoice(null);
    }
  };

  const handleClose = () => {
    onClose({
      gameKey: key,
      penaltyCount: (rounds - aiWins) >= minPassRate ? 0 : minPassRate - (rounds - aiWins),
      completed: playerWins,
      total: rounds,
      totalTime: rounds,
      mistakes: rounds - playerWins,
      userid,
      username,
      date: new Date(),
    });
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <DialogTitle>Rock Paper Scissors</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Win at least {minPassRate} out of {rounds} rounds!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {penalties.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Penalty for incorrect answers: <b>{penalties.map(p => p.name).join(", ")}</b>
            </Alert>
          )}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Round: {currentRound} / {rounds}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Your Wins: {playerWins}</Typography>
          <Typography variant="body2">AI Wins: {aiWins}</Typography>
          <Typography variant="body2">Draws: {draws}</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Choose your move:</Typography>
          <Grid container spacing={2} justifyContent="center">
            {choices.map(choice => (
              <Grid item key={choice}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={finished || lastPlayerChoice !== null}
                  onClick={() => handlePlayerChoice(choice)}
                  sx={{ minWidth: 90, fontSize: 18 }}
                >
                  {choice}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>
        {lastPlayerChoice && lastAiChoice && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              You chose: <b>{lastPlayerChoice}</b>
            </Typography>
            <Typography variant="body2">
              AI chose: <b>{lastAiChoice}</b>
            </Typography>
          </Box>
        )}
        {message && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="subtitle1"
              color={
                finished && aiWins < minPassRate
                  ? "success.main"
                  : finished
                    ? "error.main"
                    : "text.primary"
              }
            >
              {finished ? `You lost ${aiWins} of ${rounds} rounds. Pass rate required: ${minPassRate}` : message}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {finished && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RockPaperScissorsDialog;